import { defineComponent, PropType } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";

import { VendorsService } from "../../../../../../services/vendor-service";
import { EnumsService } from "../../../../../../services/enums.service";
import { IGetVendorSlim } from "../../../../../../resources-vue/vue-interfaces/i-vendor";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IVendor } from "../../../../../../resources-vue/vue-interfaces/i-vendor";
import { DMEVendor } from "../../../../../../resources-vue/vue-models/dme-vendor";
import { EnumMap } from "../../../../../../common/enum-map";
import { formatZipCode, formatPhoneNumber } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { useConvertToPendingPal } from "../../../store/useConvertToPendingPal";

import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import FormatName from "../../../../../../resources-vue/vue-custom-elements/FormatName/FormatName.vue";
import EmptyTextHandler from "../../../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";

export default defineComponent({
    components: {
        TypeaheadInput,
        FormatName,
        EmptyTextHandler
    },
    props: {
        cardTitle: {
            type: String,
            default: ""
        },
        providerId: {
            type: String,
            default: ""
        },
        vendorId: {
            type: String,
            default: null
        },
        patientVendors: { type: Array as PropType<string[]>, default: [] },
        vendorTypes: { type: Array as PropType<string[]>, default: [] }
    },
    data() {
        return {
            resultsCount: 5,
            _ignoreIdChange: false,
            loadingVendorDetails: false,
            vendorDetails: new DMEVendor() as IVendor,
            vendorTypesEnumMap: new EnumMap([]),
            vendor: null as ITypeaheadOptions,
            vendorTypeValues: null as number[],
            _vendorsService: null,
            _enumService: null,
            formatZipCode,
            formatPhoneNumber
        };
    },
    computed: {
        ...mapState(useConvertToPendingPal, ["accountId", "application"])
    },
    watch: {
        async vendor(newValue: ITypeaheadOptions) {
            if (newValue) {
                let vendorId = newValue.value;
                this._ignoreIdChange = true;
                this.$emit("update:vendorId", vendorId);
                await this.loadVendorData(vendorId);
            } else {
                this.vendor = null;
                this.vendorDetails = null;
                this.loadingVendorDetails = false;
                if (this.vendorId) {
                    const patientVendors = this.patientVendors.filter(
                        (patientVendor: string) => patientVendor !== this.vendorId
                    );
                    this.$emit("update:patientVendors", patientVendors);
                }
            }
        }
    },
    emits: ["update:vendorId", "update:patientVendors"],
    created() {
        this._vendorsService = Container.instance.get(VendorsService);
        this._enumService = Container.instance.get(EnumsService);
    },
    async mounted() {
        if (this.vendorId) {
            await this.vendorIdChanged(this.vendorId);
        }
        let vendorsTypeEnum = await this._enumService.getVendorTypes();
        this.vendorTypesEnumMap = new EnumMap(vendorsTypeEnum);
        this.vendorTypeValues = this.vendorTypes.map((vendor) => Number(this.vendorTypesEnumMap.getEnumValue(vendor)));
    },
    methods: {
        async getVendorList(filter: string) {
            // Test this endpoint
            // Needs fixing, values don't match for both DME vendor and supplies vendor
            let DMEVendorsResult = await this._vendorsService.getVendorByTerm({
                name: filter,
                types: this.vendorTypeValues,
                providerIds: [this.providerId],
                application: this.application,
                accountId: this.accountId
            });
            return this.vendorSlimToTypeahead(DMEVendorsResult.splice(0, this.resultsCount));
        },
        vendorSlimToTypeahead(vendors: IGetVendorSlim[]) {
            return vendors.map((vendor: IGetVendorSlim) => ({ name: vendor.name, value: vendor.id }));
        },
        async loadVendorData(vendorId: string) {
            if (vendorId) {
                this.loadingVendorDetails = true;
                this.vendorDetails = await this._vendorsService.getVendorById({
                    id: vendorId,
                    application: this.application,
                    accountId: this.accountId
                });
                this.loadingVendorDetails = false;
            }
        },
        vendorChanged(data: ITypeaheadOptions) {
            this.vendor = data;
        },
        async vendorIdChanged(vendor: string) {
            if (this._ignoreIdChange) {
                this._ignoreIdChange = false;
                return;
            }
            if (vendor) {
                await this.loadVendorData(vendor);
                this.vendor = {
                    name: this.vendorDetails.name,
                    value: this.vendorDetails.id
                };
            } else {
                this.vendor = null;
                this.vendorDetails = null;
                this.loadingVendorDetails = false;
            }
        }
    }
});
